// Shared project data for Portfolio, AllProjects, and ProjectDetail pages
//
// IMAGE SETUP:
// Place project images in: client/public/assets/projects/
// Each project needs:
//   - {id}.webp          → Thumbnail (used in listings, ~1200x800)
//   - {id}-hero.webp     → Hero image (used in detail page, ~1920x1080)
//   - {id}-1.webp, -2.webp, -3.webp → Gallery images (~800x600)
//
// Example: for "rowaaq-academy":
//   client/public/assets/projects/rowaaq-academy.webp
//   client/public/assets/projects/rowaaq-academy-hero.webp
//   client/public/assets/projects/rowaaq-academy-1.webp

export interface ProjectSummary {
  id: string;
  title: string;
  description: string;
  image: string;
  tags: string[];
  category: string;
  duration: string;
  durationAr: string;
  teamSize: string;
  teamSizeAr: string;
  featured: boolean;
}

export interface ProjectDetailData {
  title: { en: string; ar: string };
  description: { en: string; ar: string };
  heroImage: string;
  category: { en: string; ar: string };
  client: { en: string; ar: string };
  duration: { en: string; ar: string };
  teamSize: { en: string; ar: string };
  platform: { en: string; ar: string };
  technologies: string[];
  challenge: { en: string; ar: string };
  solution: { en: string; ar: string };
  results: { en: string[]; ar: string[] };
  features: { en: string[]; ar: string[] };
  gallery: string[];
  nextProject?: string;
  prevProject?: string;
}

// ─── Project Summaries (used by Portfolio & AllProjects) ─────────────────────

export const projects: ProjectSummary[] = [

  {
    id: "rowaaq-lms",
    title: "rowaaqLms",
    description: "rowaaqLmsDesc",
    image: "https://blog.coursify.me/wp-content/uploads/2018/02/learning-management-system-lms-cover.jpg",
    tags: ["Next.js", "TypeScript", "Go", "PostgreSQL", "Redis"],
    category: "saas",
    duration: "3 months",
    durationAr: "3 أشهر",
    teamSize: "2 developers",
    teamSizeAr: "مطورَين",
    featured: false,
  },
  {
    id: "nafees-jms",
    title: "nafeesJms",
    description: "nafeesJmsDesc",
    image: "https://i.ibb.co/rR8tCYH0/Screenshot-2026-02-11-163759.webp",
    tags: ["Flutter", "Go", "PostgreSQL", "Redis"],
    category: "saas",
    duration: "Ongoing",
    durationAr: "مستمر",
    teamSize: "2 developers",
    teamSizeAr: "مطورَين",
    featured: false,
  },
  {
    id: "nafees-24",
    title: "nafees24",
    description: "nafees24Desc",
    image: "https://i.ibb.co/YKKFwdc/image.webp",
    tags: ["Flutter", "Dart", "Go", "REST API"],
    category: "mobile",
    duration: "2 months",
    durationAr: "شهرين",
    teamSize: "2 developers",
    teamSizeAr: "مطورَين",
    featured: false,
  },
  {
    id: "discount-locker",
    title: "discountLocker",
    description: "discountLockerDesc",
    image: "https://i.ibb.co/DfKSzthc/Screenshot-2026-02-11-162924.webp",
    tags: ["Flutter", "Shopify API", "Firebase"],
    category: "mobile",
    duration: "5 months",
    durationAr: "5 أشهر",
    teamSize: "2 developers",
    teamSizeAr: "مطورَين",
    featured: false,
  },
  {
    id: "rowaaq-academy",
    title: "rowaaqAcademy",
    description: "rowaaqAcademyDesc",
    image: "https://i.ibb.co/mCvVkYXq/Screenshot-2026-02-11-163006.webp",
    tags: ["Next.js", "TypeScript", "Tailwind CSS", "Go"],
    category: "web",
    duration: "2 months",
    durationAr: "شهرين",
    teamSize: "2 developers",
    teamSizeAr: "مطورَين",
    featured: false,
  }
];

// Homepage shows 4 projects (featured first, non-featured, non-featured, featured)
const homepageProjectIds = [
  "rowaaq-academy",
  "nafees-jms",
  "rowaaq-lms",
  "discount-locker",
];

export const homepageProjects = projects.filter((p) =>
  homepageProjectIds.includes(p.id)
);

// ─── Filter Categories (used by AllProjects) ─────────────────────────────────

export const categories = [
  { id: "all", en: "All Projects", ar: "جميع المشاريع" },
  { id: "mobile", en: "Mobile Apps", ar: "تطبيقات الجوال" },
  { id: "web", en: "Web Platforms", ar: "منصات الويب" },
  { id: "saas", en: "SaaS Platforms", ar: "منصات SaaS" },
];

// ─── Project Detail Data (used by ProjectDetail page) ────────────────────────

export const projectsDetail: Record<string, ProjectDetailData> = {
  "rowaaq-academy": {
    title: { en: "Rowaaq Academy Website", ar: "موقع أكاديمية رُواق" },
    description: {
      en: "A modern, professional website for an educational institution specializing in programming and technology education for kids aged 8-16.",
      ar: "موقع إلكتروني احترافي وعصري لمؤسسة تعليمية متخصصة في تعليم البرمجة والتكنولوجيا للأطفال من سن 8 إلى 16 سنة.",
    },
    heroImage: "https://i.ibb.co/mCvVkYXq/Screenshot-2026-02-11-163006.webp",
    category: { en: "Web Platform", ar: "منصة ويب" },
    client: { en: "Rowaaq Academy", ar: "أكاديمية رُواق" },
    duration: { en: "2 months", ar: "شهرين" },
    teamSize: { en: "2 developers", ar: "مطورَين" },
    platform: { en: "Web (Responsive)", ar: "ويب (متجاوب)" },
    technologies: ["Next.js", "TypeScript", "Tailwind CSS", "Go", "PostgreSQL"],
    challenge: {
      en: "Rowaaq Academy is an educational institution specializing in teaching programming, design, and artificial intelligence to children and teenagers aged 8 to 16. They needed a professional website that showcases their courses and services in an engaging way that appeals to their target audience — parents and students — with a seamless user experience that simplifies registration and communication.",
      ar: "أكاديمية رواق هي أكاديمية متخصصة في تعليم البرمجة والتصميم والذكاء الاصطناعي للأطفال والمراهقين من سن 8 لـ 16 سنة. كانت محتاجة موقع ويب احترافي يعرض خدماتها وكورساتها بشكل جذاب يناسب الفئة المستهدفة (أولياء الأمور والطلاب)، مع تجربة مستخدم سلسة تسهل عملية التسجيل والتواصل.",
    },
    solution: {
      en: "We developed a modern, professional website using Next.js with an attractive design tailored for children, teenagers, and parents. The website presents available courses, learning paths, and age groups in an organized and easy-to-browse format. The design is fully responsive across all devices with fast performance and an excellent user experience.",
      ar: "قمنا بتطوير موقع ويب احترافي وحديث باستخدام Next.js مع تصميم جذاب يناسب الأطفال والمراهقين وأولياء الأمور. الموقع يعرض الكورسات المتاحة والمسارات التعليمية والفئات العمرية بشكل منظم وسهل التصفح. التصميم Responsive بالكامل ويعمل على جميع الأجهزة مع أداء سريع وتجربة مستخدم ممتازة.",
    },
    results: {
      en: [
        "3+ Learning Paths",
        "8-16 Age Groups",
        "Result-First + Gamification",
        "CSTA/ISTE Aligned",
      ],
      ar: [
        "3+ مسارات تعليمية",
        "فئات 8-16 سنة",
        "Result-First + Gamification",
        "متوافق مع CSTA/ISTE",
      ],
    },
    features: {
      en: [
        "Attractive homepage with modern design tailored to the target audience",
        "Course and learning path showcase pages",
        "Course categorization by age group (8–16 years)",
        "\"About Us\" page presenting the academy's vision and mission",
        "Student and parent registration system",
        "Comprehensive educational blog",
        "FAQ page",
        "Direct communication via WhatsApp",
        "Parent and student testimonials section",
        "Fully responsive design across all devices",
        "SEO Optimized",
      ],
      ar: [
        "صفحة رئيسية جذابة بتصميم حديث يناسب الفئة المستهدفة",
        "صفحات عرض الكورسات والمسارات التعليمية",
        "تصنيف الكورسات حسب الفئة العمرية (8–16 سنة)",
        "صفحة \"من نحن\" تعرض رؤية ورسالة الأكاديمية",
        "نظام تسجيل وإنشاء حسابات للطلاب وأولياء الأمور",
        "مدونة تعليمية متكاملة",
        "صفحة الأسئلة الشائعة (FAQ)",
        "نظام تواصل مباشر عبر WhatsApp",
        "آراء وتقييمات أولياء الأمور والطلاب",
        "تصميم متجاوب يعمل على جميع الأجهزة",
        "محسّن لمحركات البحث (SEO)",
      ],
    },
    gallery: [
      "/assets/projects/rowaaq-academy-1.webp",
      "/assets/projects/rowaaq-academy-2.webp",
      "/assets/projects/rowaaq-academy-3.webp",
    ],
    nextProject: "nafees-jms",
    prevProject: "rowaaq-lms",
  },

  "nafees-jms": {
    title: { en: "Nafees JMS", ar: "نفيس لإدارة محلات المجوهرات" },
    description: {
      en: "A comprehensive SaaS platform combining ERP, POS, and CRM capabilities specifically designed for gold and silver jewelry store management.",
      ar: "منصة SaaS متكاملة تجمع بين نظام ERP و POS و CRM مصممة خصيصًا لإدارة محلات المجوهرات والذهب والفضة.",
    },
    heroImage: "https://i.ibb.co/rR8tCYH0/Screenshot-2026-02-11-163759.webp",
    category: { en: "SaaS Platform", ar: "منصة SaaS" },
    client: { en: "Ibtkary — Internal Product", ar: "ابتكاري — منتج داخلي" },
    duration: { en: "Ongoing", ar: "مستمر" },
    teamSize: { en: "2 developers", ar: "مطورَين" },
    platform: {
      en: "Cross-platform (Web + Mobile)",
      ar: "متعدد المنصات (ويب + موبايل)",
    },
    technologies: [
      "Flutter",
      "Dart",
      "Go",
      "PostgreSQL",
      "Redis",
      "Docker",
      "REST API",
    ],
    challenge: {
      en: "The gold and silver market in Egypt relies heavily on traditional and paper-based systems for store management. Merchants needed a comprehensive system to manage all aspects of their business — inventory, sales, accounting, and customer relationships — while addressing the unique requirements of the gold sector, which deals with weight, karat, and constantly fluctuating real-time prices.",
      ar: "سوق الذهب والفضة في مصر يعتمد بشكل كبير على الأنظمة التقليدية والورقية في إدارة المحلات. التجار محتاجين نظام شامل يدير كل جوانب العمل من المخزون والمبيعات والحسابات وعلاقات العملاء، مع مراعاة خصوصية قطاع الذهب اللي بيتعامل بالوزن والعيار وأسعار متغيرة لحظيًا.",
    },
    solution: {
      en: "We developed a comprehensive SaaS platform specifically designed for gold and silver jewelry store management, combining three core systems: ERP for inventory management, accounting, and supplier management; POS for advanced point-of-sale with gold calculations; and CRM for customer relationships and loyalty programs.",
      ar: "قمنا بتطوير منصة SaaS متكاملة مخصصة لإدارة محلات الذهب والفضة تجمع بين ثلاثة أنظمة رئيسية: ERP لإدارة المخزون والحسابات والموردين، POS لنظام نقاط بيع متقدم مع حسابات الذهب، وCRM لإدارة علاقات العملاء وبرامج الولاء.",
    },
    results: {
      en: [
        "Multi-tenant SaaS",
        "Gold & Silver Stores",
        "ERP+POS+CRM Integrated",
        "Web + Mobile",
      ],
      ar: [
        "SaaS متعدد المستأجرين",
        "محلات الذهب والفضة",
        "ERP+POS+CRM متكامل",
        "ويب + موبايل",
      ],
    },
    features: {
      en: [
        "Inventory management by weight, karat, and piece",
        "Supplier and purchase management",
        "Financial and accounting reports",
        "Multi-branch management",
        "User permissions and role management",
        "Fast and user-friendly point of sale",
        "Automatic craftsmanship fee and discount calculation",
        "Real-time gold and silver price updates",
        "Invoice printing",
        "Exchange, scrap, and soldering operations support",
        "Customer profiles and transaction history",
        "Installment and commitment tracking",
        "Notifications and alerts",
        "Customer reports and analytics",
      ],
      ar: [
        "إدارة المخزون بالوزن والعيار والقطعة",
        "إدارة الموردين والمشتريات",
        "التقارير المالية والمحاسبية",
        "إدارة الفروع المتعددة",
        "صلاحيات المستخدمين والأدوار",
        "نقطة بيع سريعة وسهلة الاستخدام",
        "حساب المصنعية والخصم التلقائي",
        "تحديث الأسعار اللحظية للذهب والفضة",
        "طباعة الفواتير",
        "دعم عمليات الاستبدال والكسر واللحام",
        "ملفات العملاء وتاريخ المعاملات",
        "متابعة الأمانات والالتزامات",
        "التنبيهات والإشعارات",
        "تقارير العملاء والتحليلات",
      ],
    },
    gallery: [
      "/assets/projects/nafees-jms-1.webp",
      "/assets/projects/nafees-jms-2.webp",
      "/assets/projects/nafees-jms-3.webp",
    ],
    nextProject: "nafees-24",
    prevProject: "rowaaq-academy",
  },

  "nafees-24": {
    title: { en: "Nafees 24", ar: "نفيس 24 لأسعار الذهب والعملات" },
    description: {
      en: "A cross-platform mobile application providing real-time gold, silver, and currency prices for the Egyptian market.",
      ar: "تطبيق موبايل متعدد المنصات يوفر أسعار الذهب والفضة والعملات اللحظية للسوق المصري.",
    },
    heroImage: "https://i.ibb.co/YKKFwdc/image.webp",
    category: { en: "Mobile App", ar: "تطبيق موبايل" },
    client: { en: "Ibtkary — Internal Product", ar: "ابتكاري — منتج داخلي" },
    duration: { en: "2 months", ar: "شهرين" },
    teamSize: { en: "2 developers", ar: "مطورَين" },
    platform: {
      en: "Cross-platform (Android & iOS)",
      ar: "متعدد المنصات (أندرويد و iOS)",
    },
    technologies: [
      "Flutter",
      "Dart",
      "Go",
      "PostgreSQL",
      "REST API",
      "Real-time Data",
    ],
    challenge: {
      en: "In the Egyptian gold market, consumers and merchants need a reliable, real-time source for tracking gold, silver, and currency prices accurately and quickly. Existing apps in the market are either slow to update, inaccurate, or overloaded with ads. There was a clear need for a fast, accurate, and user-friendly app specifically serving the Egyptian market.",
      ar: "في سوق الذهب المصري، المستهلكين والتجار بيحتاجوا مصدر موثوق ولحظي لمتابعة أسعار الذهب والفضة والعملات بشكل دقيق وسريع. التطبيقات الموجودة في السوق إما بطيئة في التحديث أو غير دقيقة أو مليانة إعلانات. كان فيه حاجة لتطبيق سريع، دقيق، وسهل الاستخدام يخدم السوق المصري تحديدًا.",
    },
    solution: {
      en: "We developed Nafees 24 as a cross-platform mobile application that provides real-time gold, silver, and currency prices for the Egyptian market. Built with Flutter technology to ensure high performance and a smooth user experience on both Android and iOS. The app integrates with the broader Nafees ecosystem as a standalone product that delivers value to end users.",
      ar: "قمنا بتطوير تطبيق نفيس 24 كتطبيق موبايل cross-platform يقدم أسعار الذهب والفضة والعملات اللحظية في السوق المصري. التطبيق مبني بتكنولوجيا Flutter لضمان أداء عالي وتجربة مستخدم سلسة على Android وiOS. التطبيق بيتكامل مع منظومة نفيس الأكبر كمنتج مستقل يضيف قيمة للمستخدم النهائي.",
    },
    results: {
      en: [
        "Android + iOS",
        "Egyptian Market",
        "Real-time Updates",
        "Gold + Silver + Currencies",
      ],
      ar: [
        "أندرويد + iOS",
        "السوق المصري",
        "تحديثات لحظية",
        "ذهب + فضة + عملات",
      ],
    },
    features: {
      en: [
        "Real-time gold prices across all karats (24, 22, 21, 18)",
        "Real-time silver prices",
        "Foreign currency exchange rates against the Egyptian Pound",
        "Automatic and continuous price updates",
        "Arabic-first user interface",
        "Price change notifications",
        "Price movement charts and graphs",
        "Modern design with fast performance",
      ],
      ar: [
        "أسعار الذهب اللحظية بجميع الأعيرة (24، 22، 21، 18)",
        "أسعار الفضة اللحظية",
        "أسعار العملات الأجنبية مقابل الجنيه المصري",
        "تحديث تلقائي ومستمر للأسعار",
        "واجهة عربية سهلة الاستخدام",
        "إشعارات بتغيرات الأسعار",
        "رسوم بيانية لتتبع حركة الأسعار",
        "تصميم حديث وأداء سريع",
      ],
    },
    gallery: [
      "/assets/projects/nafees-24-1.webp",
      "/assets/projects/nafees-24-2.webp",
      "/assets/projects/nafees-24-3.webp",
    ],
    nextProject: "discount-locker",
    prevProject: "nafees-jms",
  },

  "discount-locker": {
    title: { en: "The Discount Locker", ar: "The Discount Locker" },
    description: {
      en: "A fully-featured e-commerce mobile application for luxury brands at discounted prices, integrated with Shopify, serving the Dubai and international market.",
      ar: "تطبيق موبايل تجارة إلكترونية متكامل للعلامات التجارية الفاخرة بأسعار مخفضة، متكامل مع Shopify، يخدم سوق دبي والسوق الدولي.",
    },
    heroImage: "https://i.ibb.co/DfKSzthc/Screenshot-2026-02-11-162924.webp",
    category: { en: "Mobile App / E-Commerce", ar: "تطبيق موبايل / تجارة إلكترونية" },
    client: {
      en: "The Discount Locker (Dubai, UAE)",
      ar: "The Discount Locker (دبي، الإمارات)",
    },
    duration: { en: "5 months", ar: "5 أشهر" },
    teamSize: { en: "2 developers", ar: "مطورَين" },
    platform: {
      en: "Cross-platform (Android & iOS)",
      ar: "متعدد المنصات (أندرويد و iOS)",
    },
    technologies: ["Flutter", "Dart", "Shopify API", "Firebase", "REST API"],
    challenge: {
      en: "A Dubai-based client owns an online store specializing in selling global luxury brands at discounted prices. They needed a professional mobile app to showcase their products across footwear, electronics, clothing, accessories, and perfumes. The client already had a Shopify website and needed an app that fully integrates with it while providing a seamless shopping experience with international shipping support.",
      ar: "عميل من دبي يمتلك متجر إلكتروني متخصص في بيع البراندات العالمية بأسعار مخفضة، كان محتاج تطبيق موبايل احترافي يعرض منتجاته من الأحذية والإلكترونيات والملابس والإكسسوارات والعطور. العميل عنده موقع على Shopify وكان محتاج تطبيق يتكامل مع الموقع ويوفر تجربة تسوق سلسة للعملاء مع دعم الشحن الدولي.",
    },
    solution: {
      en: "We developed a fully integrated e-commerce app using Flutter with complete Shopify API integration for product and order management. The app provides a smooth shopping experience with professional product displays and support for multiple languages and currencies. Available on Google Play and App Store with over 1,000 downloads.",
      ar: "قمنا بتطوير تطبيق e-commerce متكامل باستخدام Flutter مع تكامل كامل مع Shopify API لإدارة المنتجات والطلبات. التطبيق يوفر تجربة تسوق سلسة مع عرض احترافي للمنتجات ودعم لعدة لغات وعملات. التطبيق متاح على Google Play وApp Store مع أكثر من 1000 تحميل.",
    },
    results: {
      en: [
        "1,000+ Downloads",
        "Android + iOS",
        "Dubai + International",
        "Major Luxury Brands",
      ],
      ar: [
        "1,000+ تحميل",
        "أندرويد + iOS",
        "دبي + دولي",
        "علامات تجارية فاخرة",
      ],
    },
    features: {
      en: [
        "Product browsing by category (Footwear, Electronics, Clothes, Accessories, Perfumes)",
        "Brand-based browsing (HOKA, Adidas, Nike, OFF WHITE, and more)",
        "Shopping cart and secure checkout",
        "Deals, discounts, and Flash Sales",
        "Advanced search and filtering",
        "Order tracking",
        "Multi-currency and multi-language support (English & Arabic)",
        "Full Shopify integration",
        "Wishlist",
        "International shipping",
        "Return and exchange policy",
      ],
      ar: [
        "عرض المنتجات حسب الفئات (أحذية، إلكترونيات، ملابس، إكسسوارات، عطور)",
        "عرض حسب البراندات (HOKA, Adidas, Nike, OFF WHITE، وغيرها)",
        "نظام سلة التسوق والدفع الآمن",
        "عروض وخصومات وFlash Sales",
        "نظام بحث متقدم وفلترة",
        "تتبع الطلبات",
        "دعم عدة عملات ولغات (إنجليزي وعربي)",
        "تكامل كامل مع Shopify",
        "قائمة المفضلة (Wishlist)",
        "الشحن الدولي",
        "سياسة إرجاع واستبدال",
      ],
    },
    gallery: [
      "/assets/projects/discount-locker-1.webp",
      "/assets/projects/discount-locker-2.webp",
      "/assets/projects/discount-locker-3.webp",
    ],
    nextProject: "rowaaq-lms",
    prevProject: "nafees-24",
  },

  "rowaaq-lms": {
    title: { en: "Rowaaq LMS", ar: "نظام رُواق التعليمي LMS" },
    description: {
      en: "A comprehensive Learning Management System with gamification and assessment capabilities designed for student lifecycle management for ages 8-16.",
      ar: "نظام إدارة تعلّم شامل مع عناصر تحفيزية (Gamification) وتقييم مصمم لإدارة دورة حياة الطالب للأعمار 8-16 سنة.",
    },
    heroImage: "https://blog.coursify.me/wp-content/uploads/2018/02/learning-management-system-lms-cover.jpg",
    category: { en: "SaaS / LMS Platform", ar: "منصة SaaS / LMS" },
    client: { en: "Rowaaq Academy", ar: "أكاديمية رُواق" },
    duration: { en: "3 months", ar: "3 أشهر" },
    teamSize: { en: "2 developers", ar: "مطورَين" },
    platform: { en: "Web Application", ar: "تطبيق ويب" },
    technologies: [
      "Next.js",
      "TypeScript",
      "Tailwind CSS",
      "Go",
      "PostgreSQL",
      "Redis",
      "REST API",
    ],
    challenge: {
      en: "Rowaaq Academy needed a comprehensive Learning Management System (LMS) to fully manage the educational process — from student registration and course management to progress tracking and certificate issuance. The academy operates with a Gamification and Result-First approach, offering interactive live sessions (individual and small groups), and the system needed to support all of this along with student level assessment, improvement plans, and flexible level transitions.",
      ar: "أكاديمية رواق كانت محتاجة نظام إدارة تعلم (LMS) متكامل يدير العملية التعليمية بالكامل من تسجيل الطلاب وإدارة الكورسات وتتبع التقدم وحتى إصدار الشهادات. الأكاديمية بتشتغل بأسلوب Gamification وResult-First مع حصص تفاعلية مباشرة (فردي ومجموعات صغيرة)، وكان لازم النظام يدعم كل ده مع تحديد مستوى الطلاب وخطط التقوية والانتقال بين المستويات.",
    },
    solution: {
      en: "We developed a fully integrated custom LMS tailored to Rowaaq Academy's needs. The system manages the complete student lifecycle from registration and level assessment, through session management and practical projects, to certificate issuance. Built with modern technologies and fully integrated with the academy's website.",
      ar: "قمنا بتطوير نظام LMS متكامل مخصص لاحتياجات أكاديمية رواق. النظام يدير دورة حياة الطالب بالكامل من لحظة التسجيل وتحديد المستوى مرورًا بإدارة الحصص والمشاريع العملية وحتى إصدار الشهادات. مبني بتكنولوجيا حديثة ويتكامل بشكل كامل مع موقع الأكاديمية.",
    },
    results: {
      en: [
        "Fully Integrated LMS",
        "8-16 Years",
        "Gamification + Result-First",
        "CSTA/ISTE Aligned",
      ],
      ar: [
        "LMS متكامل بالكامل",
        "8-16 سنة",
        "Gamification + Result-First",
        "متوافق مع CSTA/ISTE",
      ],
    },
    features: {
      en: [
        "Student registration and account management",
        "Student level assessment system",
        "Student progress and completion tracking",
        "Digital portfolio for each student",
        "Flexible improvement and level transition plans",
        "Course and curriculum management",
        "Classification by learning paths (Programming, Design, AI)",
        "Practical project creation and management per level",
        "Unplugged activities for preparation",
        "Interactive live sessions (individual and small groups)",
        "Certified instructor management and scheduling",
        "Session scheduling and time management",
        "Gamification system (points, badges, levels)",
        "Completion certificate issuance system",
        "Comprehensive admin dashboard",
        "Performance and progress reports",
        "Payment and subscription management",
      ],
      ar: [
        "تسجيل الطلاب وإدارة الحسابات",
        "نظام تحديد مستوى الطالب",
        "تتبع تقدم الطالب ونسب الإنجاز",
        "Portfolio رقمي لكل طالب",
        "خطط تقوية وانتقال مرن بين المستويات",
        "إدارة الكورسات والمناهج التعليمية",
        "تصنيف حسب المسارات (برمجة، تصميم، ذكاء اصطناعي)",
        "إنشاء وإدارة المشاريع العملية لكل مستوى",
        "محتوى Unplugged Activities للتهيئة",
        "نظام الحصص المباشرة التفاعلية (فردي ومجموعات صغيرة)",
        "إدارة المدربين المعتمدين وجداولهم",
        "جدولة الحصص وإدارة التوقيتات",
        "نظام Gamification لتحفيز الطلاب (نقاط، بيجيز، مستويات)",
        "نظام إصدار شهادات الإتمام",
        "لوحة تحكم شاملة للإدارة",
        "تقارير الأداء والتقدم",
        "إدارة المدفوعات والاشتراكات",
      ],
    },
    gallery: [
      "/assets/projects/rowaaq-lms-1.webp",
      "/assets/projects/rowaaq-lms-2.webp",
      "/assets/projects/rowaaq-lms-3.webp",
    ],
    nextProject: "rowaaq-academy",
    prevProject: "discount-locker",
  },
};

// Default project for unknown IDs
export function getProjectDetail(id: string | undefined): ProjectDetailData | undefined {
  return id && Object.prototype.hasOwnProperty.call(projectsDetail, id) ? projectsDetail[id] : undefined;
}

export const defaultProject: ProjectDetailData = {
  title: { en: "Project Not Found", ar: "المشروع غير موجود" },
  description: {
    en: "This project doesn't exist.",
    ar: "هذا المشروع غير موجود.",
  },
  heroImage: "/assets/projects/default.webp",
  category: { en: "N/A", ar: "غير متوفر" },
  client: { en: "N/A", ar: "غير متوفر" },
  duration: { en: "N/A", ar: "غير متوفر" },
  teamSize: { en: "N/A", ar: "غير متوفر" },
  platform: { en: "N/A", ar: "غير متوفر" },
  technologies: [],
  challenge: { en: "", ar: "" },
  solution: { en: "", ar: "" },
  results: { en: [], ar: [] },
  features: { en: [], ar: [] },
  gallery: [],
};
